/**
 * Dashboard 壳层布局态唯一组合（侧栏 + 顶栏槽位 + 移动抽屉）。
 *
 * - `sidebarCollapsed`：用户桌面偏好（cookie）
 * - `navCollapsed`：实际 UI 收起（小屏恒 false）
 *
 * @see docs/dashboard-shell-state.md
 */
import { computed, ref, watch } from 'vue'
import { useHydrationSafeMobile } from '~/composables/useHydrationSafeMobile'
import { useDashboardSidebarCollapsed } from '~/composables/useDashboardSidebarCollapsed'
import { useDashboardSubNavCollapsed } from '~/composables/useDashboardSubNavCollapsed'
import {
  resolveMobileSidebarWidthPx,
  resolveSidebarWidthPx,
  SIDEBAR_MOBILE_DRAWER_WIDTH_PX,
  syncSidebarCollapsedDomAttr,
  writeSidebarUserPrefClient,
} from '~/utils/dashboard-sidebar-state'

export function useDashboardShellLayout() {
  const route = useRoute()
  const { isMobile, syncIsMobileFromWindow } = useHydrationSafeMobile()
  const sidebar = useDashboardSidebarCollapsed()
  const subNav = useDashboardSubNavCollapsed()

  const mobileNavOpen = useState('lip-mobile-nav-open', () => false)
  const layoutShellReady = ref(false)

  const mobileShell = computed(() => layoutShellReady.value && isMobile.value)
  /** 双栏侧栏：桌面恒为展开（仅二级面板可 icon 轨收起） */
  const navCollapsed = computed(() => false)

  const sidebarWidthPx = computed(() => {
    if (hideLayoutSidebar.value) return 0
    if (mobileShell.value) {
      return resolveMobileSidebarWidthPx(subNav.subNavCollapsed.value)
    }
    return resolveSidebarWidthPx(false, subNav.subNavCollapsed.value)
  })

  const lightShell = computed(() => route.meta.lightShell === true)
  const hideLayoutSidebar = computed(() => route.meta.hideLayoutSidebar === true)
  const showLayoutFooter = computed(() => route.meta.hideLayoutFooter !== true)

  watch(
    [sidebarWidthPx, mobileShell],
    ([width, mobile]) => {
      if (!import.meta.client) return
      document.documentElement.style.setProperty(
        '--dashboard-sidebar-width',
        mobile && width >= SIDEBAR_MOBILE_DRAWER_WIDTH_PX ? '0px' : `${width}px`,
      )
    },
    { immediate: true },
  )

  watch(
    [sidebar.sidebarCollapsed, mobileShell],
    ([wholeCollapsed, mobile]) => {
      if (!import.meta.client || mobile) return
      // 旧版「整栏收起」偏好迁移为二级 icon 轨
      if (wholeCollapsed) {
        subNav.subNavCollapsed.value = true
        writeSidebarUserPrefClient('expanded')
        sidebar.sidebarCollapsed.value = false
      }
      syncSidebarCollapsedDomAttr(false)
    },
    { immediate: true },
  )

  function markLayoutShellReady() {
    layoutShellReady.value = true
  }

  function applySidebarLayoutForViewport() {
    if (!import.meta.client || typeof window === 'undefined') return
    if (isMobile.value) {
      mobileNavOpen.value = false
    }
  }

  return {
    ...sidebar,
    ...subNav,
    isMobile,
    syncIsMobileFromWindow,
    mobileNavOpen,
    layoutShellReady,
    mobileShell,
    navCollapsed,
    sidebarWidthPx,
    lightShell,
    hideLayoutSidebar,
    showLayoutFooter,
    markLayoutShellReady,
    applySidebarLayoutForViewport,
  }
}
